// ─────────────────────────────────────────────────────────────
// Device Graph General Constants
// ─────────────────────────────────────────────────────────────

const oneMinuteInMs = 60000;

// ─────────────────────────────────────────────────────────────
// Device Graph Color Helpers
// ─────────────────────────────────────────────────────────────

export const colorList = [
  'rgb(255, 0, 0)', // Red
  'rgb(255, 165, 0)', // Orange
  'rgb(0, 128, 0)', // Green
  'rgb(45, 190, 190)', // Cyan
  'rgb(0, 0, 255)', // Blue
  'rgb(128, 0, 128)', // Purple
  'rgb(182, 53, 175)', // Pink
];

/**
 * Build a map of colors per device id.
 * @param {Array<import('../../types/MonitoringStation_types').Device>} devices - Array of device objects to assign colors for.
 * @returns {Object<number,string>} Object where keys are device `id` (number) and values are CSS color strings (e.g. 'rgb(255, 0, 0)').
 */
export const buildColorMap = (devices) => {
  const colorMap = {};

  devices.forEach((device, index) => {
    if (device && device.id != null) {
      colorMap[device.id] = colorList[index % colorList.length];
    }
  });

  return colorMap;
};

// ─────────────────────────────────────────────────────────────
// Device Graph Time Helpers
// ─────────────────────────────────────────────────────────────

export const formatGraphTimeRangeLabel = (startTime, endTime) => {
  try {
    const fmt = new Intl.DateTimeFormat([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    if (!startTime || !endTime) return null;
    return `${fmt.format(new Date(startTime))} – ${fmt.format(new Date(endTime))}`;
  } catch {
    return null;
  }
};

// Creates the timestamps used for the x-axis labels
/**
 * Build a Set of normalized Date objects between `startTime` and `endTime` at 1-minute steps.
 * @param {number|Date} startTime - Start time (timestamp in ms or Date). The returned set will include timestamps starting at this time (normalized to minute precision).
 * @param {number|Date} endTime - End time (timestamp in ms or Date). This time will be included in the returned set as the final timestamp.
 * @returns {Set<Date>} A Set of `Date` objects normalized to minute/second precision (seconds and milliseconds set to 0). Values are ordered by insertion and represent each minute between start and end.
 */
export const buildTimeStampSet = (startTime, endTime) => {
  const timestampSet = new Set(); // Stores the x-axis labels (uses a set to avoid duplicate timestamps)

  // Iterate from startTime to endTime in 1-minute increments
  for (let time = startTime; time < endTime; time += oneMinuteInMs) {
    const date = new Date(time); // Convert timestamp to Date object
    date.setSeconds(0, 0); // Sets the seonds and milliseconds of the date to zero to normalize the timestamps
    timestampSet.add(date); // Add the normalized date to the set
  }

  timestampSet.add(new Date(endTime)); // Add the end time

  return timestampSet;
};

// ─────────────────────────────────────────────────────────────
// Device Graph Alert Level Helpers
// ─────────────────────────────────────────────────────────────

// Gets the highest threshold value crossed (alert level)
export const getHighestThresholdValueCrossed = (depth, settings) => {
  if (depth >= settings?.depthDet3) return 3; // Highest threshold/alert level
  if (depth >= settings?.depthDet2) return 2; // Mid-level threshold/alert level
  if (depth >= settings?.depthDet1) return 1; // Lowest threshold/alert level
  return 0; // No threshold crossed
};

// Adds a flood alert event if a threshold is crossed and returns the alert level (0-3)
/**
 * Adds a flood alert event if a threshold is crossed and returns the alert level (0-3).
 * @param {number} waterDepth - The measured water depth value for the device (units should match `deviceSettings` thresholds).
 * @param {object} deviceSettings - Settings object containing threshold values (e.g., `depthDet1`, `depthDet2`, `depthDet3`). Used to determine alert level.
 * @param {Array<{timestamp:number,level:number}>} floodAlertEvents - Mutable array of existing flood alert events; if a new alert is detected it will be pushed into this array or update an existing event's level.
 * @param {object} deviceData - The device data object for the current datapoint; expected to include a `ts` (timestamp) property used as the event timestamp.
 * @returns {number} The computed alert level: 0 = none, 1 = low, 2 = medium, 3 = high.
 */
export const recordAlertEventAndGetLevel = (
  waterDepth,
  deviceSettings,
  floodAlertEvents,
  deviceData,
) => {
  const alertLevel = getHighestThresholdValueCrossed(waterDepth, deviceSettings);

  // If there is an alert level, record the event
  if (alertLevel > 0) {
    // O(1) Map lookup instead of O(n) array.find
    const existing = floodAlertEvents.get(deviceData.ts);

    // If the timestamp doesn't exist, add a new event
    if (!existing) {
      floodAlertEvents.set(deviceData.ts, {
        timestamp: deviceData.ts,
        level: alertLevel,
      });
    } else if (alertLevel > existing.level) {
      // Sets the old existing event to the higher alert level
      existing.level = alertLevel;
    }
  }

  return alertLevel;
};

// ─────────────────────────────────────────────────────────────
// Device Graph General Helpers
// ─────────────────────────────────────────────────────────────

// Build a map of devices by alias for easy lookup
/**
 * Build a map of devices by alias for easy lookup.
 * @param {Array<import('../../types/MonitoringStation_types').Device>} devices - Array of device objects which must include an `alias` string property.
 * @returns {Map<string, import('../../types/MonitoringStation_types').Device>} Map keyed by device `alias` with the device object as the value for fast lookups.
 */
export const buildStageDeviceMap = (devices) => {
  const deviceMap = new Map();
  devices.forEach((device) => {
    // Key by stringified `id` to match selection map keys
    if (device && device.id != null) {
      deviceMap.set(String(device.id), device);
    }
  });
  return deviceMap;
};

export const isSelectedAndHasDataWithinTimeRange = (
  deviceId,
  selectedDeviceMap,
  stageDeviceMap,
  filterWithinTimeRange,
) => {
  // Skips unselected devices
  if (!selectedDeviceMap[deviceId]) return false;

  const device = stageDeviceMap.get(String(deviceId));
  // Skips devices with no data
  if (!device?.data?.length) return false;

  // Skips devices with no data in time range (within startTime and endTime)
  return device.data.some(filterWithinTimeRange);
};

// Gets the y-axis value
/**
 * Get the y-axis numeric value for a device datapoint based on the selected data type.
 * @param {object} deviceData - Device datapoint object containing fields such as `sensordepths`, `depth1`, `h2oTemp`, `baro`, `battery`, `rssi`, `intensity`, `rainfall`, and `tips`.
 * @param {string} selectedDataType - The selected metric to display on the y-axis. Expected values: 'sensordepths', 'depths', 'temp', 'baro', 'battery', 'cell signal', 'intensity', 'rainfall', 'tips'.
 * @param {boolean} unitType - Unit flag: `true` to return metric values, `false` to return imperial-converted values where applicable.
 * @returns {number} Numeric value to plot on the y-axis (converted as necessary). Returns 0 when the selected type is unrecognized or data is missing.
 */
export const getYAxisValue = (deviceData, selectedDataType, unitType) => {
  const { sensordepths, depth1, h2oTemp, baro, battery, rssi, intensity, rainfall, tips } =
    deviceData;

  switch (selectedDataType) {
    case 'sensordepths':
      return unitType ? sensordepths * 0.0254 : sensordepths / 12;
    case 'depths':
      return unitType ? depth1 * 0.0254 : depth1 / 12;
    case 'temp':
      return unitType ? h2oTemp : (h2oTemp * 9) / 5 + 32;
    case 'baro':
      return unitType ? baro : baro * 0.02952998;
    case 'battery':
      return battery;
    case 'cell signal':
      return rssi;
    case 'intensity':
      return intensity;
    case 'rainfall':
      return rainfall;
    case 'tips':
      return tips;
    default:
      return 0;
  }
};
